import React, { useState } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import LanguageIcon from '@mui/icons-material/Language';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EnquiryModal from './EnquiryModal';

const services = [
  {
    icon: <ComputerIcon sx={{ fontSize: 30 }} />,
    title: 'IT Consulting',
    description: 'We map your current operations and build a practical digital roadmap — no jargon, no bloated proposals. Just a clear plan that fits your team and budget.',
    tags: ['Digital Transformation', 'Process Audit', 'Roadmapping'],
    featured: true,
  },
  {
    icon: <LanguageIcon sx={{ fontSize: 26 }} />,
    title: 'Website Development',
    description: 'Custom, fast, mobile-first websites built to convert visitors into leads.',
    tags: ['React', 'SEO-Ready', 'CMS'],
    featured: false,
  },
  {
    icon: <CloudIcon sx={{ fontSize: 26 }} />,
    title: 'DevOps Support',
    description: 'Cloud infra, CI/CD pipelines, and server management so your systems stay online.',
    tags: ['AWS', 'Docker', 'CI/CD'],
    featured: false,
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 26 }} />,
    title: 'Software Licensing',
    description: 'Microsoft 365, Google Workspace, Zoho, Antivirus & MDM — procured and configured.',
    tags: ['M365', 'Google Workspace', 'MDM'],
    featured: false,
  },
  {
    icon: <DevicesIcon sx={{ fontSize: 26 }} />,
    title: 'Hardware Sourcing',
    description: 'Enterprise laptops, firewalls, switches, and routers — sourced and deployed.',
    tags: ['Laptops', 'Networking', 'Firewalls'],
    featured: false,
  },
];

const Services = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const open = (title) => { setSelectedService(title); setModalOpen(true); };
  const featured = services.find(s => s.featured);
  const rest = services.filter(s => !s.featured);

  return (
    <Box id="services" sx={{ bgcolor: '#FFFFFF', py: { xs: 6, md: 12 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>IT Services</Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '2rem', sm: '2.4rem', md: '3rem' }, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              Everything your<br />business needs.
            </Typography>
            <Typography sx={{ color: '#6B7280', maxWidth: 340, lineHeight: 1.8, fontSize: '0.95rem' }}>
              Beyond our SaaS products, we handle the complete IT layer — from strategy to the hardware on your desk.
            </Typography>
          </Box>
        </Box>

        {/* Bento grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 2 }}>
          {/* Featured */}
          <Box onClick={() => open(featured.title)} sx={{
            gridColumn: { md: 'span 2' },
            bgcolor: '#F8F9FF', border: '1px solid #E5E7EB',
            borderRadius: '16px', p: { xs: 3, md: 4 },
            cursor: 'pointer', position: 'relative', overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': { borderColor: '#6366F1', transform: 'translateY(-3px)', boxShadow: '0 20px 48px rgba(99,102,241,0.1)' },
          }}>
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <Box sx={{ width: 56, height: 56, borderRadius: '12px', bgcolor: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366F1', mb: 3 }}>
              {featured.icon}
            </Box>
            <Typography sx={{ fontWeight: 800, color: '#0F1117', fontSize: '1.4rem', mb: 1.5, letterSpacing: '-0.01em' }}>{featured.title}</Typography>
            <Typography sx={{ color: '#6B7280', lineHeight: 1.8, mb: 3, maxWidth: 460, fontSize: '0.95rem' }}>{featured.description}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {featured.tags.map(t => (
                <Box key={t} sx={{ px: 2, py: 0.5, borderRadius: '100px', bgcolor: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.18)', color: '#818CF8', fontSize: '0.75rem', fontWeight: 600 }}>{t}</Box>
              ))}
            </Box>
            <Button endIcon={<ArrowForwardIcon />} sx={{ color: '#6366F1', fontWeight: 700, textTransform: 'none', px: 0, '&:hover': { bgcolor: 'transparent', color: '#818CF8' } }}>
              Enquire Now
            </Button>
          </Box>

          {/* Small cards */}
          {rest.map((s) => (
            <Box key={s.title} onClick={() => open(s.title)} sx={{
              bgcolor: '#F8F9FF', border: '1px solid #E5E7EB',
              borderRadius: '16px', p: 3, cursor: 'pointer',
              display: 'flex', flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': { borderColor: '#6366F1', transform: 'translateY(-3px)', boxShadow: '0 16px 40px rgba(99,102,241,0.08)' },
            }}>
              <Box sx={{ width: 46, height: 46, borderRadius: '10px', bgcolor: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366F1', mb: 2 }}>
                {s.icon}
              </Box>
              <Typography sx={{ fontWeight: 800, color: '#0F1117', fontSize: '1rem', mb: 1 }}>{s.title}</Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', lineHeight: 1.7, mb: 2, flexGrow: 1 }}>{s.description}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {s.tags.map(t => (
                  <Box key={t} sx={{ px: 1.5, py: 0.3, borderRadius: '100px', bgcolor: '#F3F4F6', border: '1px solid #E5E7EB', color: '#6B7280', fontSize: '0.7rem', fontWeight: 500 }}>{t}</Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
      <EnquiryModal open={modalOpen} onClose={() => setModalOpen(false)} service={selectedService} />
    </Box>
  );
};

export default Services;
